import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/main.scss";

const formatPrice = (value) =>
  `${siteConfig.currency.symbol}${Number(value).toFixed(2)} ${siteConfig.currency.code}`;

function Main() {
  const { isWishlisted, toggleWishlist } = useContext(CartContext);
  const shorts = productData
    .filter((product) => product.category === "Shorts")
    .slice(0, 8);
  const tops = productData
    .filter((product) => ["Shirts", "Tees"].includes(product.category))
    .slice(0, 4);
  const journalStories = [
    {
      title: "Built slowly, worn often",
      excerpt:
        "Why everyday gear gets better when the cloth, construction, and purpose are considered together.",
      image: "/img/about-workshop.webp",
      to: "/about",
    },
    {
      title: "A field note from the coast",
      excerpt:
        "An unhurried afternoon, a small camp, and the layers that earn a permanent place in the car.",
      image: "/img/about-coast.webp",
      to: "/about",
    },
    {
      title: "The road is the occasion",
      excerpt:
        "Clothing designed for detours, changing weather, and the ordinary rituals between destinations.",
      image: "/img/story-forest.webp",
      to: "/shop",
    },
  ];

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const renderProductCard = (product) => (
    <article className="catalog-card" key={product.id}>
      <div className="catalog-card__media">
        <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
          />
          <div className="catalog-card__sizes" aria-hidden="true">
            {product.size.slice(0, 5).map((size) => (
              <span key={size}>{size}</span>
            ))}
          </div>
        </Link>
        {product.compareAtPrice && <span className="catalog-card__sale">Sale</span>}
        <button
          className={`catalog-card__wish${
            isWishlisted(product.id) ? " is-active" : ""
          }`}
          type="button"
          aria-label={
            isWishlisted(product.id)
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
          aria-pressed={isWishlisted(product.id)}
          onClick={() => toggleWishlist(product.id)}
        >
          <span aria-hidden="true">♡</span>
        </button>
      </div>
      <div className="catalog-card__copy">
        <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
          {product.name}
        </Link>
        <p>
          {product.compareAtPrice && (
            <del>{formatPrice(product.compareAtPrice)}</del>
          )}
          <span className={product.compareAtPrice ? "is-sale" : ""}>
            {formatPrice(product.price)}
          </span>
        </p>
        <small>{product.colors.length} colors</small>
      </div>
    </article>
  );

  return (
    <div className="home-page">
      <main>
        <section className="home-hero" aria-label={siteConfig.tagline}>
          <img
            src="/img/hero-coast.webp"
            alt="A traveler in an orange shell overlooking the coast"
            fetchpriority="high"
          />
        </section>

        <section className="home-products" aria-labelledby="shorts-season-title">
          <div className="home-section-heading">
            <h1 id="shorts-season-title">Shorts season</h1>
            <Link className="button button--primary" to="/shop?category=Shorts">
              Shop now
            </Link>
          </div>
          <div className="home-product-rail">{shorts.map(renderProductCard)}</div>
        </section>

        <section className="home-story">
          <div className="home-story__image">
            <img
              src="/img/story-forest.webp"
              alt="An outdoorsman preparing for a forest journey"
              loading="lazy"
            />
          </div>
          <div className="home-story__copy">
            <p className="eyebrow">Since 2011</p>
            <h2>{siteConfig.brandName}, rebuilt</h2>
            <p>
              We make useful clothing for the space between city streets and
              the trailhead—familiar pieces refined with better movement,
              durable cloth, and details that have a reason to exist.
            </p>
            <Link to="/about" onClick={scrollToTop}>
              Full story <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </section>

        <section className="home-mood" aria-label="Made for days outside">
          <img
            src="/img/about-coast.webp"
            alt="Friends spending a quiet afternoon on the coast"
            loading="lazy"
          />
        </section>

        <section className="home-products" aria-labelledby="shirts-title">
          <div className="home-section-heading">
            <h2 id="shirts-title">Shirts for all occasions</h2>
            <Link className="button button--primary" to="/shop?category=Shirts">
              Shop now
            </Link>
          </div>
          <div className="home-product-grid">{tops.map(renderProductCard)}</div>
        </section>

        <section
          className="home-portals"
          aria-label={`Explore ${siteConfig.brandName}`}
        >
          <Link to="/about" onClick={scrollToTop}>
            <img
              src="/img/about-workshop.webp"
              alt="A maker examining an outdoor jacket in a workshop"
              loading="lazy"
            />
            <span>About us</span>
          </Link>
          <a href="#journal-title">
            <img
              src="/img/hero-coast.webp"
              alt="A field story from the Pacific coast"
              loading="lazy"
            />
            <span>Field journal</span>
          </a>
        </section>

        <section className="home-journal" aria-labelledby="journal-title">
          <div className="home-journal__heading">
            <p className="eyebrow">Field journal</p>
            <h2 id="journal-title">Stories behind the gear</h2>
          </div>
          <div className="home-journal__grid">
            {journalStories.map((story) => (
              <article key={story.title}>
                <Link to={story.to} onClick={scrollToTop}>
                  <img src={story.image} alt="" loading="lazy" />
                  <h3>{story.title}</h3>
                  <p>{story.excerpt}</p>
                  <span>Read more ↗</span>
                </Link>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default Main;
